import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from "@/views/academy/api/apiRequests";
import { AcademyNavigatorApiEndpoints } from "@/views/academy/common/academynavigator/AcademyNavigatorApiEndpoints";
import { StorageItemTypes } from "@/views/academy/enums/StorageItemTypes";
import { i18n } from "@/plugins/i18n";
import { StorageItemDetailsApiEndpoints } from "@/views/academy/common/storageitemdetails/StorageItemDetailsApiEndpoints";
import { StorageItemFileTypes } from "@/views/academy/enums/StorageItemFileTypes";
import { getBase64Thumbnail } from "@/helpers/thumnail.utils";
import { scrollToListItem } from "@/helpers/scrollToListItem";
import { prepareQueryParams } from "@/helpers/prepareQueryParams";
import axios from "axios";
import { dataURLtoFile } from "@/helpers/dataURLToFile";
import { ApiRequestHeaderTypes } from "@/views/academy/enums/ApiRequestHeaderTypes";
import { PRESIGNED_URL_KEYS_TO_UPLOAD_NAMES } from "../enums/PresignedURLKeysToUpload";
import { createImageThumbnail } from "@/helpers/createImageThumbnail";

const state = () => ({
  storageItems: null,
  assignments: [],
  breadcrumbs: null,
  activeOrganization: null,
  uploadingStorageItems: [],
  storageItemDetails: null,
  isUploadFilesDialogOpened: false,
  isAddFolderDialogOpened: false,
  isFormDialogOpened: false,
  isFormCreateDialog: false,
  isTodoDialogOpened: false,
  isTodoCreateDialog: false,
  isShareStorageItemDialogOpened: false,
  shareStorageItemSearchValue: "",
  isDeleteStorageItemDialogOpened: false,
  isUploadingFilesFinished: true,
  currentStorageBreadcrumbs: [],
  storageSearchInputValue: "",
  activePlayer: null,
  lastAddedStorageItemId: "",
  mediaBrowserUploadedItems: [],
  isMediaBrowserDialogOpened: false,
  isInputFocused: false,
  lastDirectory: null,
  isCreateAssignmentDialogOpened: false,
  createAssignmentDialogType: null,
  isCallSearchRequest: true,
  levels: null,
  isStorageContentLoading: false,
  isTodoDialogPreview: false,
  isFormDialogPreview: false,
  isAdminFoldersRootLevel: false,
  linkModalOpen: false,
  hasAccessToUploadStorageItem: false,
  isMoveStorageItemModalOpen: false,
  isMultiSelect: false,
  multiSelectItems: []
});

const getters = {
  storageContent: (state: {
    storageItems: any;
    breadcrumbs: any;
    accessType: string;
    virtual: boolean;
  }) => {
    const { storageItems, breadcrumbs, accessType, virtual } = state;
    return {
      storageItems,
      breadcrumbs,
      accessType,
      virtual
    };
  },

  activeOrganization: (state: { activeOrganization: object }) =>
    state.activeOrganization,

  activePlayer: (state: { activePlayer: object }) => state.activePlayer,

  uploadingStorageItems: (state: { uploadingStorageItems: any }) =>
    state.uploadingStorageItems,

  storageItemDetails: (state: { storageItemDetails: object }) =>
    state.storageItemDetails,

  isFormDialogOpened: (state: { isFormDialogOpened: boolean }) =>
    state.isFormDialogOpened,

  isFormCreateDialog: (state: { isFormCreateDialog: boolean }) =>
    state.isFormCreateDialog,

  isTodoDialogOpened: (state: { isTodoDialogOpened: boolean }) =>
    state.isTodoDialogOpened,

  isTodoCreateDialog: (state: { isTodoCreateDialog: boolean }) =>
    state.isTodoCreateDialog,

  isUploadFilesDialogOpened: (state: { isUploadFilesDialogOpened: boolean }) =>
    state.isUploadFilesDialogOpened,

  isAddFolderDialogOpened: (state: { isAddFolderDialogOpened: boolean }) =>
    state.isAddFolderDialogOpened,

  isShareStorageItemDialogOpened: (state: {
    isShareStorageItemDialogOpened: boolean;
  }) => state.isShareStorageItemDialogOpened,

  shareStorageItemSearchValue: (state: {
    shareStorageItemSearchValue: string;
  }) => state.shareStorageItemSearchValue,

  isDeleteStorageItemDialogOpened: (state: {
    isDeleteStorageItemDialogOpened: boolean;
  }) => state.isDeleteStorageItemDialogOpened,

  isCreateAssignmentDialogOpened: (state: {
    isCreateAssignmentDialogOpened: boolean;
  }) => state.isCreateAssignmentDialogOpened,

  isUploadingFilesFinished: (state: { isUploadingFilesFinished: boolean }) =>
    state.isUploadingFilesFinished,

  currentStorageBreadcrumbs: (state: { currentStorageBreadcrumbs: any }) =>
    state.currentStorageBreadcrumbs,

  storageSearchInputValue: (state: { storageSearchInputValue: string }) =>
    state.storageSearchInputValue,

  lastAddedStorageItemId: (state: { lastAddedStorageItemId: string }) =>
    state.lastAddedStorageItemId,

  isMediaBrowserDialogOpened: (state: {
    isMediaBrowserDialogOpened: boolean;
  }) => state.isMediaBrowserDialogOpened,

  mediaBrowserUploadedItems: (state: { mediaBrowserUploadedItems: string }) =>
    state.mediaBrowserUploadedItems,

  isInputFocused: (state: { isInputFocused: boolean }) => state.isInputFocused,

  createAssignmentDialogType: (state: { createAssignmentDialogType: any }) =>
    state.createAssignmentDialogType,

  isCallSearchRequest: (state: { isCallSearchRequest: boolean }) =>
    state.isCallSearchRequest,

  levels: (state: { levels: any }) => state.levels,

  isStorageContentLoading: (state: { isStorageContentLoading: boolean }) =>
    state.isStorageContentLoading,

  isTodoDialogPreview: (state: { isTodoDialogPreview: boolean }) =>
    state.isTodoDialogPreview,

  isFormDialogPreview: (state: { isFormDialogPreview: boolean }) =>
    state.isFormDialogPreview,

  isAdminFoldersRootLevel: (state: { isAdminFoldersRootLevel: boolean }) =>
    state.isAdminFoldersRootLevel,

  linkModalOpen: (state: { linkModalOpen: boolean }) => state.linkModalOpen,

  isMoveStorageItemModalOpen: (state: {
    isMoveStorageItemModalOpen: boolean;
  }) => state.isMoveStorageItemModalOpen,

  hasAccessToUploadStorageItem: (state: {
    hasAccessToUploadStorageItem: boolean;
  }) => state.hasAccessToUploadStorageItem,

  isMultiSelect: (state: { isMultiSelect: boolean }) => state.isMultiSelect,

  multiSelectItems: (state: { multiSelectItems: any[] }) =>
    state.multiSelectItems
};

const mutations = {
  setStorageItemDetails: (
    state: {
      storageItems: any;
      storageItemDetails: object;
    },
    payload: {
      storageItem: { storageItemId: string };
      isSidebarList: any;
      isContextMenu: any;
      isRegistration: boolean;
      id: any;
    }
  ) => {
    if (
      !payload?.isSidebarList ||
      payload.isContextMenu ||
      payload.isRegistration
    ) {
      state.storageItemDetails = payload.storageItem;
    } else {
      state.storageItemDetails = state.storageItems.find(
        (item: { id: string }) => item.id === payload.storageItem.storageItemId
      );
    }
  },

  hideStorageItemDetails: (state: { storageItemDetails: null }) => {
    state.storageItemDetails = null;
  },

  setActiveOrganization: (
    state: { activeOrganization: object },
    payload: any
  ) => {
    state.activeOrganization = payload;
  },

  setActivePlayer: (state: { activePlayer: object }, payload: any) => {
    state.activePlayer = payload;
  },

  setStorageContent: (
    state: {
      storageItems: [];
      breadcrumbs: [];
      accessType: string;
      virtual: boolean;
    },
    payload: any
  ) => {
    state.storageItems = payload.items;
    state.breadcrumbs = payload.breadcrumbs;
    state.accessType = payload.accessType;
    state.virtual = payload.virtual;
  },

  addMediaBrowserUploadedItems: (
    state: { mediaBrowserUploadedItems: Array<string> },
    payload: any
  ) => {
    state.mediaBrowserUploadedItems.push(payload);
  },

  clearMediaBrowserUploadedItems: (state: {
    mediaBrowserUploadedItems: Array<string>;
  }) => {
    state.mediaBrowserUploadedItems = [];
  },

  setStorageBreadcrumbs: (
    state: { currentStorageBreadcrumbs: any },
    payload: any
  ) => {
    state.currentStorageBreadcrumbs = payload;
  },

  setUploadingStorageFiles: (
    state: { uploadingStorageItems: [] },
    payload: any
  ) => {
    state.uploadingStorageItems = payload;
  },

  setUploadingSingleStorageFile: (
    state: { uploadingStorageItems: any[] },
    payload: any
  ) => {
    state.uploadingStorageItems = state.uploadingStorageItems.map(
      (item: any) => {
        if (item.id === payload.id) {
          const { id, name, progress } = payload;
          return {
            id,
            name,
            progress
          };
        }
        const { id, name, progress } = item;
        return {
          id,
          name,
          progress
        };
      }
    );
  },

  clearUploadingStorageFiles: (state: { uploadingStorageItems: [] }) => {
    state.uploadingStorageItems = [];
  },

  setStorageItemNameOrHyperLink: (
    state: { storageItemDetails: any },
    payload: any
  ) => {
    const { name, hyperLink } = payload;
    state.storageItemDetails = {
      ...state.storageItemDetails,
      name,
      hyperLink
    };
  },

  toggleUploadFilesDialog: (
    state: { isUploadFilesDialogOpened: boolean },
    payload: { isUploadFilesDialogOpened: any }
  ) => {
    const { isUploadFilesDialogOpened } = payload;
    state.isUploadFilesDialogOpened = !!isUploadFilesDialogOpened;
  },

  toggleAddFolderDialog: (
    state: { isAddFolderDialogOpened: boolean },
    payload: { isAddFolderDialogOpened: any }
  ) => {
    const { isAddFolderDialogOpened } = payload;
    state.isAddFolderDialogOpened = !!isAddFolderDialogOpened;
  },

  toggleFormDialog: (
    state: {
      isFormDialogOpened: boolean;
      isFormCreateDialog: boolean;
      isFormDialogPreview: boolean;
    },
    payload: {
      isFormDialogOpened: boolean;
      isFormCreateDialog: boolean;
      isFormDialogPreview: boolean;
    }
  ) => {
    const {
      isFormDialogOpened,
      isFormCreateDialog,
      isFormDialogPreview
    } = payload;
    state.isFormDialogOpened = isFormDialogOpened;
    state.isFormCreateDialog = isFormCreateDialog;
    state.isFormDialogPreview = isFormDialogPreview;
  },

  toggleTodoDialog: (
    state: {
      isTodoDialogOpened: boolean;
      isTodoCreateDialog: boolean;
      isTodoDialogPreview: boolean;
    },
    payload: {
      isTodoDialogOpened: boolean;
      isTodoCreateDialog: boolean;
      isTodoDialogPreview: boolean;
    }
  ) => {
    const {
      isTodoDialogOpened,
      isTodoCreateDialog,
      isTodoDialogPreview
    } = payload;
    state.isTodoDialogOpened = isTodoDialogOpened;
    state.isTodoCreateDialog = isTodoCreateDialog;
    state.isTodoDialogPreview = isTodoDialogPreview;
  },

  removeUploadingStorageItems: (
    state: { uploadingStorageItems: any[] },
    payload: any
  ) => {
    state.uploadingStorageItems = state.uploadingStorageItems.filter(
      (item, index) => index !== payload
    );
  },

  toggleShareStorageItemDialog: (
    state: { isShareStorageItemDialogOpened: boolean },
    payload: { isShareStorageItemDialogOpened: boolean }
  ) => {
    const { isShareStorageItemDialogOpened } = payload;
    state.isShareStorageItemDialogOpened = isShareStorageItemDialogOpened;
  },

  setShareStorageItemSearch: (
    state: { shareStorageItemSearchValue: string },
    payload: string
  ) => {
    state.shareStorageItemSearchValue = payload;
  },

  toggleDeleteStorageItemDialog: (
    state: { isDeleteStorageItemDialogOpened: boolean },
    payload: { isDeleteStorageItemDialogOpened: boolean }
  ) => {
    const { isDeleteStorageItemDialogOpened } = payload;
    state.isDeleteStorageItemDialogOpened = isDeleteStorageItemDialogOpened;
  },

  toggleCreateAssignmentDialog: (
    state: {
      isCreateAssignmentDialogOpened: boolean;
      createAssignmentDialogType: any;
    },
    payload: {
      isCreateAssignmentDialogOpened: boolean;
      createAssignmentDialogType: any;
    }
  ) => {
    const {
      isCreateAssignmentDialogOpened,
      createAssignmentDialogType
    } = payload;
    state.isCreateAssignmentDialogOpened = isCreateAssignmentDialogOpened;
    state.createAssignmentDialogType = createAssignmentDialogType || null;
  },

  toggleMoveStorageItemModal: (
    state: {
      isMoveStorageItemModalOpen: boolean;
    },
    payload: {
      isMoveStorageItemModalOpen: boolean;
    }
  ) => {
    const { isMoveStorageItemModalOpen } = payload;
    state.isMoveStorageItemModalOpen = isMoveStorageItemModalOpen;
  },

  clearStorageItemDetails: (state: { storageItemDetails: null }) => {
    state.storageItemDetails = null;
  },

  isUploadingFilesFinished: (
    state: { isUploadingFilesFinished: boolean },
    payload: { isUploadingFilesFinished: boolean }
  ) => {
    state.isUploadingFilesFinished = payload.isUploadingFilesFinished;
  },

  setStorageSearchInputValue: (
    state: { storageSearchInputValue: string | { target: { value: string } } },
    payload: { target: { value: string } }
  ) => {
    state.storageSearchInputValue = payload;
  },

  setLastAddedStorageItemId: (
    state: { lastAddedStorageItemId: any },
    payload: any
  ) => {
    state.lastAddedStorageItemId = payload;
  },

  toggleMediaBrowserDialog: (
    state: { isMediaBrowserDialogOpened: boolean },
    payload: { isMediaBrowserDialogOpened: boolean }
  ) => {
    const { isMediaBrowserDialogOpened } = payload;
    state.isMediaBrowserDialogOpened = isMediaBrowserDialogOpened;
  },

  setStorageSearchInputFocused: (
    state: { isInputFocused: boolean },
    payload: any
  ) => {
    state.isInputFocused = payload;
  },

  setFavoriteOrRecentDetails: (
    state: { storageItemDetails: any },
    payload: any
  ) => {
    state.storageItemDetails = payload;
  },

  setIsCallSearchRequest: (
    state: { isCallSearchRequest: any },
    payload: any
  ) => {
    state.isCallSearchRequest = payload.isCallSearchRequest;
  },

  setLevels: (state: { levels: any }, payload: { levels: any }) => {
    state.levels = payload.levels;
  },

  setStorageContentLoader: (
    state: { isStorageContentLoading: boolean },
    payload: { isStorageContentLoading: boolean }
  ) => {
    state.isStorageContentLoading = payload.isStorageContentLoading;
  },

  clearStorageItems: (state: { storageItems: null; accessType: null }) => {
    state.storageItems = null;
    state.accessType = null;
  },

  setIsAdminFoldersRootLevel: (
    state: { isAdminFoldersRootLevel: boolean },
    payload: { isAdminFoldersRootLevel: boolean }
  ) => {
    state.isAdminFoldersRootLevel = payload.isAdminFoldersRootLevel;
  },

  toggleStorageLinkModal: (
    state: { linkModalOpen: boolean },
    payload: { linkModalOpen: boolean }
  ) => {
    state.linkModalOpen = payload.linkModalOpen;
  },

  setAccessToUploadStorageItem: (
    state: { hasAccessToUploadStorageItem: boolean },
    payload: { hasAccessToUploadStorageItem: boolean }
  ) => {
    state.hasAccessToUploadStorageItem = payload.hasAccessToUploadStorageItem;
  },

  setIsMultiSelect: (
    state: { isMultiSelect: boolean },
    payload: { isMultiSelect: boolean }
  ) => {
    state.isMultiSelect = payload.isMultiSelect;
  },

  setMultiSelectItems: (
    state: { multiSelectItems: any[] },
    payload: { multiSelectItems: any[] }
  ) => {
    state.multiSelectItems = payload.multiSelectItems;
  },

  clearMultiSelect: (state: {
    multiSelectItems: any[];
    isMultiSelect: boolean;
  }) => {
    state.multiSelectItems = [];
    state.isMultiSelect = false;
  }
};

const actions = {
  setStorageItemDetails: (
    context: {
      commit: (
        arg0: string,
        arg1: {
          item?: any;
          id?: any;
          storageItem?: any;
          isSidebarList?: any;
          isContextMenu?: any;
          organizationId?: any;
          playerId?: any;
          storageItemId?: any;
        },
        arg2?: { root: boolean } | undefined
      ) => void;
      dispatch: (arg0: string, arg1: any, arg2?: { root: boolean }) => void;
    },
    payload: {
      item?: any;
      id?: any;
      storageItem?: any;
      isSidebarList?: any;
      isContextMenu?: any;
      organizationId?: any;
      playerId?: any;
    }
  ) => {
    const {
      storageItem,
      isSidebarList,
      isContextMenu,
      organizationId,
      playerId
    } = payload;

    if (isSidebarList) {
      context.dispatch("fetchStorageContent", {
        organizationId,
        parentId: storageItem?.parentId,
        playerId
      });
    }
    setTimeout(() => context.commit("setStorageItemDetails", payload), 300);

    context.dispatch(
      "storageItemDetails/fetchStorageItemFavoriteState",
      {
        storageItemId: isSidebarList
          ? payload.storageItem.storageItemId
          : storageItem.id
      },
      { root: true }
    );
    if (!isContextMenu) {
      context.commit(
        "root/openRightSidebar",
        { storageItemId: payload.id },
        { root: true }
      );
    }
  },

  hideStorageItemDetails: (
    context: {
      commit: (
        arg0: string,
        arg1: {} | null,
        arg2?: { root: boolean } | undefined
      ) => void;
      dispatch: (arg0: string, arg1: null, arg2: { root: boolean }) => void;
    },
    payload: any
  ) => {
    context.commit("hideStorageItemDetails", payload);
    context.commit(
      "storageItemDetails/setSearchPeopleAndTeamsToShareInDetailsSuccess",
      {},
      { root: true }
    );
    context.commit("root/closeRightSidebar", null, { root: true });
  },

  setActiveOrganization: (
    context: { commit: (arg0: string, arg1: any) => void },
    payload: any
  ) => {
    context.commit("setActiveOrganization", payload);
  },

  setActivePlayer: (
    context: { commit: (arg0: string, arg1: any) => void },
    payload: any
  ) => {
    context.commit("setActivePlayer", payload);
  },

  addMediaBrowserUploadedItems: (
    context: { commit: (arg0: string, arg1: string) => void },
    payload: string
  ) => {
    context.commit("addMediaBrowserUploadedItems", payload);
  },

  setFavoriteOrRecentDetails: (
    context: {
      commit: (
        arg0: string,
        arg1: null,
        arg2?: { root: boolean } | undefined
      ) => void;
      dispatch: (arg0: string, arg1: any, arg2: { root: boolean }) => void;
    },
    payload: any
  ) => {
    context.commit("setFavoriteOrRecentDetails", payload);
    context.dispatch(
      "storageItemDetails/fetchStorageItemFavoriteState",
      { storageItemId: payload.id },
      { root: true }
    );
    context.commit("root/openRightSidebar", null, { root: true });
  },

  fetchStorageContent: async (
    context: {
      rootGetters: any;
      getters: any;
      commit: (arg0: string, arg1?: any) => void;
      dispatch(
        addRecentStorageItemInPlayerContext: string,
        data: any,
        root?: any
      ): void;
    },
    payload: {
      organizationId: any;
      parentId: string;
      searchValue: any;
      playerId: any;
      storageItemId: string;
      callback?: Function;
      ownerId: string;
      isFavoriteOrRecentItemContext: boolean;
      sharedWithMe: boolean;
      orgAdmins: boolean;
      tags: [];
    }
  ) => {
    const {
      organizationId,
      parentId,
      searchValue,
      playerId,
      storageItemId,
      callback,
      isFavoriteOrRecentItemContext,
      sharedWithMe,
      orgAdmins,
      tags
    } = payload;

    const query = sharedWithMe
      ? prepareQueryParams({
          sharedWithMe,
          filter: searchValue,
          orgAdmins,
          tags
        })
      : prepareQueryParams({
          storageItemParentId: parentId,
          sharedWithMe,
          filter: searchValue,
          orgAdmins,
          tags
        });

    const playerStorage = playerId ? `/player/${playerId}` : "";

    const fetchStorageItemsApiEndpoint = `${AcademyNavigatorApiEndpoints.FETCH_STORAGE_ORGANIZATION_ITEMS}/${organizationId}${playerStorage}${query}`;

    try {
      const res: any = await getRequest(fetchStorageItemsApiEndpoint);

      if (callback) {
        callback(res.data);
      } else {
        context.commit("setStorageContentLoader", {
          isStorageContentLoading: true
        });
        context.commit("clearStorageItems");
        context.commit("setStorageContent", {
          ...res.data,
          breadcrumbs: res?.data?.breadcrumbs?.length
            ? res?.data?.breadcrumbs
            : parentId
            ? context.getters?.currentStorageBreadcrumbs
            : null
        });
        if (res.data?.breadcrumbs?.length) {
          context.commit("setStorageBreadcrumbs", res.data.breadcrumbs);
        }
      }

      if (isFavoriteOrRecentItemContext) {
        const recentOrFavoriteStorageItem = res?.data?.items?.find(
          (i: { id: string }) => i?.id === storageItemId
        );
        context.dispatch(
          "setFavoriteOrRecentDetails",
          recentOrFavoriteStorageItem
        );
      }

      if (playerId && storageItemId) {
        context.dispatch("addRecentStorageItemInPlayerContext", {
          organizationId,
          playerId,
          storageItemId
        });
      } else if (!playerId) {
        context.dispatch("academyNavigator/fetchRecentStorageItems", null, {
          root: true
        });
      }
    } finally {
      context.commit("setStorageContentLoader", {
        isStorageContentLoading: false
      });
    }
  },

  addStorageItem: async (
    context: {
      getters: any;
      commit: (arg0: string, arg1: any) => void;
      dispatch: (
        arg0: string,
        arg1: any,
        arg2?: { root: boolean } | undefined
      ) => void;
    },
    payload: {
      name: any;
      organizationId: any;
      type: any;
      parentId: any;
      fileMetadataId: any;
      index: any;
      callback?: Function;
      sharedWithMe: boolean;
      hyperLink?: string;
    }
  ) => {
    context.commit("setLastAddedStorageItemId", "");
    try {
      const {
        name,
        organizationId,
        type,
        parentId,
        fileMetadataId,
        callback,
        sharedWithMe,
        hyperLink
      } = payload;
      const body = {
        description: null,
        fileMetadataId: fileMetadataId || null,
        name,
        organizationId,
        parentId: parentId || null,
        type,
        hyperLink: hyperLink || null
      };
      const res: any = await postRequest(
        AcademyNavigatorApiEndpoints.ADD_STORAGE_ORGANIZATION_ITEM,
        body
      );

      if (res.status === "success") {
        context.commit("setLastAddedStorageItemId", res?.data?.storageItemId);
        context.dispatch(
          "addMediaBrowserUploadedItems",
          res?.data?.storageItemId
        );

        if (callback) {
          callback(res?.data);
        }

        setTimeout(() => {
          context.dispatch("fetchStorageContent", {
            organizationId,
            parentId,
            sharedWithMe
          });
          if (type === StorageItemTypes.DIRECTORY) {
            context.dispatch(
              "snackbar/SHOW_SNACKBAR",
              { text: i18n.tc("snackbar_message.added_successfully") },
              {
                root: true
              }
            );
          }
          context.dispatch("academyNavigator/fetchRecentStorageItems", null, {
            root: true
          });
          setTimeout(
            () =>
              scrollToListItem(
                "storage-list",
                `storage-list-item-${res?.data?.storageItemId}`
              ),
            1000
          );
        }, 200);
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchAWSLinkForUploadFile: async (
    context: any,
    payload: { organizationId: string; files: any; successCallback: Function }
  ) => {
    try {
      const { organizationId, files, successCallback } = payload;
      const fileArr = [...files].map(item => {
        return {
          fileName: item.name,
          contentType: item.type,
          contentLength: item.size
        };
      });
      context.commit("isUploadingFilesFinished", {
        isUploadingFilesFinished: false
      });

      if (!organizationId) {
        console.error("Organization Id is required to upload file to AWS");
        return;
      }
      const endpoint = `${AcademyNavigatorApiEndpoints.UPLOAD_STORAGE_ORGANIZATION_FILE}/${organizationId}`;

      const res: any = await postRequest(endpoint, fileArr);
      if (res.status === "success") {
        successCallback && successCallback(res.data);
      }
    } catch (e) {
      console.warn(e);
    }
  },

  uploadFileToAWS: async (
    context: any,
    payload: {
      endpoint: string;
      file: any;
      id: string;
      key: string;
      isFileToAddToStorage: boolean;
      hideProgressSnackbar: boolean;
      successCallback: Function;
    }
  ) => {
    const {
      endpoint,
      file,
      id,
      key,
      successCallback,
      isFileToAddToStorage,
      hideProgressSnackbar
    } = payload;

    if (isFileToAddToStorage) {
      context.commit("setUploadingStorageFiles", [
        ...context.getters.uploadingStorageItems,
        {
          id,
          name: file.name,
          progress: 0
        }
      ]);
    }

    const config = {
      onUploadProgress: (progressEvent: { loaded: number; total: number }) => {
        const progress = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );

        context.commit("setUploadingSingleStorageFile", {
          id,
          name: file.name,
          progress
        });
      }
    };
    async function callUploadFileToAWSRequest(uploadedFile: any) {
      const {
        FILE_PRESIGNNED_URL,
        THUMBNAIL_PRESIGNED_URL
      } = PRESIGNED_URL_KEYS_TO_UPLOAD_NAMES;

      let fileToSend = null;

      switch (key) {
        case FILE_PRESIGNNED_URL:
          fileToSend = uploadedFile;
          break;
        case THUMBNAIL_PRESIGNED_URL:
          if (file?.type.includes(StorageItemFileTypes.VIDEO)) {
            fileToSend = dataURLtoFile(
              await getBase64Thumbnail(file),
              file.name
            );
          } else {
            fileToSend = await createImageThumbnail(uploadedFile);
          }
          break;
      }

      return await axios.put(endpoint, fileToSend, {
        headers: {
          [ApiRequestHeaderTypes.CONTENT_TYPE]: file.type
        },
        ...(isFileToAddToStorage ? config : [])
      });
    }

    try {
      if (!hideProgressSnackbar) {
        context.dispatch(
          "snackbar/SHOW_SNACKBAR",
          {
            text: i18n.tc("snackbar_message.upload_file_in_progress"),
            timeout: 0
          },
          { root: true }
        );
      }

      const res: any = await callUploadFileToAWSRequest(file);

      if (res.status === 200) {
        successCallback && successCallback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  uploadStorageFile: async (
    context: any,
    payload: {
      file: any;
      organizationId: any;
      parentId: any;
      sharedWithMe: boolean;
      endpoint: any;
      fileMetadataId: string;
    }
  ) => {
    const {
      file,
      organizationId,
      parentId,
      sharedWithMe,
      fileMetadataId
    } = payload;

    try {
      const addStorageItemBody = {
        description: null,
        fileMetadataId,
        name: file.name,
        organizationId,
        parentId,
        type: StorageItemTypes.FILE,
        sharedWithMe
      };

      const fetchStorageContentBody = {
        organizationId: organizationId,
        parentId,
        sharedWithMe
      };
      await context.dispatch("addStorageItem", addStorageItemBody);
      await context.dispatch("fetchStorageContent", fetchStorageContentBody);
    } catch (e) {
      console.log(e);
    }
    context.dispatch(
      "snackbar/SHOW_SNACKBAR",
      { text: i18n.tc("snackbar_message.added_successfully") },
      {
        root: true
      }
    );

    context.commit("isUploadingFilesFinished", {
      isUploadingFilesFinished: true
    });
  },

  updateStorageItemName: async (
    context: {
      commit: (arg0: string, arg1: any) => void;
      dispatch: (arg0: string, arg1: any, arg2?: object) => void;
    },
    payload: {
      name: any;
      storageItemId: any;
      organizationId: any;
      parentId: any;
      tags: [];
      tagsMode: boolean;
      sharedWithMe: boolean;
      hyperLink: string;
    }
  ) => {
    const {
      name,
      storageItemId,
      organizationId,
      parentId,
      tags,
      tagsMode,
      sharedWithMe,
      hyperLink
    } = payload;
    try {
      const body = {
        description: null,
        name,
        tags: tags || null,
        hyperLink: hyperLink || null
      };

      await putRequest(
        `${AcademyNavigatorApiEndpoints.UPDATE_STORAGE_ITEM_NAME}/${organizationId}/storage-item/${storageItemId}`,
        body
      );

      if (!tagsMode) {
        context.commit("setStorageItemNameOrHyperLink", { name, hyperLink });
        context.dispatch("fetchStorageContent", {
          organizationId,
          parentId,
          sharedWithMe
        });
      } else {
        context.dispatch(
          "storageItemDetails/fetchStorageItemTagForStorageItem",
          { storageItemId },
          { root: true }
        );
        context.dispatch(
          "snackbar/SHOW_SNACKBAR",
          { text: i18n.tc("snackbar_message.added_successfully") },
          {
            root: true
          }
        );
      }
    } catch (e) {
      console.log(e);
    }
  },

  deleteStorageItem: async (
    context: {
      dispatch: (
        arg0: string,
        arg1: {
          organizationId?: any;
          parentId?: any;
          text?: string;
          sharedWithMe?: boolean;
        } | null,
        arg2?: { root: boolean } | undefined
      ) => void;
      commit: (
        arg0: string,
        arg1?: { isDeleteStorageItemDialogOpened: boolean } | null | undefined,
        arg2?: { root: boolean } | undefined
      ) => void;
    },
    payload: {
      storageItemIds: any;
      organizationId: any;
      parentId: any;
      sharedWithMe: boolean;
    }
  ) => {
    try {
      const {
        storageItemIds,
        organizationId,
        parentId,
        sharedWithMe
      } = payload;
      console.log(storageItemIds);
      const res: any = await deleteRequest(
        `${StorageItemDetailsApiEndpoints.DELETE_STORAGE_ITEM}/${organizationId}/storage-item`,
        {
          data: storageItemIds
        }
      );
      context.commit("toggleDeleteStorageItemDialog", {
        isDeleteStorageItemDialogOpened: false
      });
      context.commit("root/closeRightSidebar", null, { root: true });
      if (res?.status === "success") {
        setTimeout(() => {
          context.dispatch("fetchStorageContent", {
            organizationId,
            parentId,
            sharedWithMe
          });
          context.dispatch("academyNavigator/fetchRecentStorageItems", null, {
            root: true
          });
          context.dispatch("academyNavigator/fetchFavoriteStorageItems", null, {
            root: true
          });
          context.dispatch(
            "snackbar/SHOW_SNACKBAR",
            { text: i18n.tc("snackbar_message.removed_successfully") },
            {
              root: true
            }
          );
          context.commit("clearStorageItemDetails");
        }, 200);
      }
    } catch (e) {
      console.log(e);
    }
  },

  updateFormTaskAction: async (
    context: {
      dispatch: (
        arg0: string,
        arg1: any,
        arg2?: { root: boolean } | undefined
      ) => void;
    },
    payload: {
      storageItemId: string;
      taskId: string;
      data: any;
      callback?: Function;
    }
  ) => {
    const { storageItemId, taskId, data, callback } = payload;
    const response: any = await putRequest(
      `${AcademyNavigatorApiEndpoints.UPDATE_FORM_TASK}/${storageItemId}/${taskId}`,
      {
        updateFormList: data
      }
    );

    context.dispatch(
      "snackbar/SHOW_SNACKBAR",
      { text: i18n.tc("snackbar_message.saved_successfully") },
      {
        root: true
      }
    );

    callback && callback(response);
  },
  getFormDetails: async (
    context: {},
    payload: { storageItemId: string; callback?: Function }
  ) => {
    const { storageItemId, callback } = payload;
    const response: unknown = await getRequest(
      `${AcademyNavigatorApiEndpoints.GET_FORM_TASK}/${storageItemId}`
    );

    callback && callback(response);
  },
  updateTodoTaskAction: async (
    context: {
      dispatch: (
        arg0: string,
        arg1: {
          organizationId?: string | undefined;
          parentId?: string | undefined;
          sharedWithMe?: boolean;
          text?: string;
        },
        arg2?: { root: boolean } | undefined
      ) => void;
    },
    payload: {
      storageItemId: string;
      taskId: string;
      data: any;
      organizationId: string | undefined;
      parentId: string | undefined;
      sharedWithMe: boolean;
      callback?: Function;
    }
  ) => {
    const { storageItemId, taskId, data, callback } = payload;

    const response: any = await putRequest(
      `${AcademyNavigatorApiEndpoints.UPDATE_TODO_TASK}/${storageItemId}/${taskId}`,
      {
        todoListItem: data
      }
    );

    context.dispatch(
      "snackbar/SHOW_SNACKBAR",
      { text: i18n.tc("snackbar_message.saved_successfully") },
      {
        root: true
      }
    );

    callback && callback(response);
  },
  getTodoDetails: async (
    context: {},
    payload: { storageItemId: string; callback?: Function }
  ) => {
    const { storageItemId, callback } = payload;
    const response: unknown = await getRequest(
      `${AcademyNavigatorApiEndpoints.GET_TODO_TASK}/${storageItemId}`
    );
    callback && callback(response);
  },

  addRecentStorageItemInPlayerContext: async (
    context: {
      dispatch: (arg0: string, arg1: null, arg2: { root: boolean }) => void;
    },
    payload: { organizationId: string; playerId: string; storageItemId: string }
  ) => {
    const { organizationId, playerId, storageItemId } = payload;
    try {
      const res: any = await postRequest(
        `${AcademyNavigatorApiEndpoints.ADD_RECENT_STORAGE_ITEM}/${organizationId}/storage/${storageItemId}/player/${playerId}`
      );
      if (res?.status === "success") {
        setTimeout(
          () =>
            context.dispatch("academyNavigator/fetchRecentStorageItems", null, {
              root: true
            }),
          200
        );
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchLevels: async (context: any, payload: { organizationId: any }) => {
    const { organizationId } = payload;
    try {
      const res: any = await getRequest(
        `${AcademyNavigatorApiEndpoints.FETCH_LEVELS}/${organizationId}`
      );
      if (res.status === "success") {
        context.commit("setLevels", { levels: res?.data });
      }
    } catch (e) {
      console.log(e);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
